import { Fragment, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EvoHeaders, EvoCells } from "@/components/employees/EvolutionCells";
import { EmployeeEvolutionChart } from "@/components/employees/EmployeeEvolutionChart";
import { Loader2, FileSpreadsheet, FileText, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight } from "lucide-react";

const fmtEur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const BASIS = [
  { v: "marca", t: "Top 25 € marca" },
  { v: "generico", t: "Top 25 € genérico" },
  { v: "unidades", t: "Top 25 más dispensadas" },
];

const Trend = ({ d }) => {
  if (d === null || d === undefined) return <span className="text-slate-400 text-xs">sin referencia</span>;
  const Icon = d > 0 ? TrendingUp : d < 0 ? TrendingDown : Minus;
  const cls = d > 0 ? "text-emerald-600" : d < 0 ? "text-rose-600" : "text-slate-400";
  return (
    <span className={`inline-flex items-center gap-1 font-mono-num font-bold ${cls}`}>
      <Icon className="h-3.5 w-3.5" />
      {d > 0 ? "+" : ""}{d.toFixed(1)} pp
    </span>
  );
};

export default function EmployeeTop25Summary({ dispId }) {
  const [basis, setBasis] = useState("marca");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(null);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    if (!dispId) return;
    let alive = true;
    setLoading(true);
    api.get(`/dispensations/${dispId}/employees/top25`, { params: { basis } })
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [dispId, basis]);

  const exportFile = async (format) => {
    setExporting(format);
    try {
      const res = await api.get(`/dispensations/${dispId}/employees/top25`, {
        params: { basis, export: format },
        responseType: "blob",
      });
      const ext = format === "excel" ? "xlsx" : "pdf";
      const blob = new Blob([res.data], {
        type: format === "excel"
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `top25_por_empleado.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al exportar");
    } finally {
      setExporting(null);
    }
  };

  const t = data?.totals || {};

  return (
    <div data-testid="employee-top25-summary">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-1">
          {BASIS.map((o) => (
            <button
              key={o.v}
              onClick={() => setBasis(o.v)}
              className={`text-xs font-bold px-3 py-1.5 rounded-lg border-2 transition-colors ${
                basis === o.v ? "border-sky-500 bg-sky-50 text-sky-700" : "border-slate-200 bg-white text-slate-500 hover:text-slate-800"
              }`}
              data-testid={`emp-top25-basis-${o.v}`}
            >
              {o.t}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => exportFile("excel")}
            disabled={!!exporting || !data}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-700 border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-lg disabled:opacity-50"
            data-testid="emp-top25-export-excel"
          >
            {exporting === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />} Excel
          </button>
          <button
            onClick={() => exportFile("pdf")}
            disabled={!!exporting || !data}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-rose-700 border border-rose-200 bg-rose-50 hover:bg-rose-100 px-3 py-2 rounded-lg disabled:opacity-50"
            data-testid="emp-top25-export-pdf"
          >
            {exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-12 flex items-center justify-center gap-2 text-slate-500 text-sm">
          <Loader2 className="h-5 w-5 animate-spin" /> Calculando el Top 25 por empleado…
        </div>
      ) : !data || !data.employees.length ? (
        <div className="py-10 text-center text-sm text-slate-500" data-testid="employee-top25-empty">
          Este estudio no tiene desglose por empleado en las moléculas del Top 25.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="emp-top25-kpi-team">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">% genérico equipo (Top 25)</div>
              <div className="font-mono-num font-bold text-lg mt-0.5">{t.team_gen_pct}%</div>
              {t.team_prev_gen_pct != null && (
                <div className="text-[11px] mt-0.5">
                  <Trend d={Number((t.team_gen_pct - t.team_prev_gen_pct).toFixed(1))} />
                  <span className="text-slate-400 ml-1">vs {data.prev_study?.label}</span>
                </div>
              )}
            </div>
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3" data-testid="emp-top25-kpi-improving">
              <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-700">Mejoran</div>
              <div className="font-mono-num font-bold text-lg mt-0.5 text-emerald-800">{t.n_improving} / {t.n_employees}</div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3" data-testid="emp-top25-kpi-worsening">
              <div className="text-[10px] uppercase tracking-widest font-bold text-rose-700">Empeoran</div>
              <div className="font-mono-num font-bold text-lg mt-0.5 text-rose-800">{t.n_worsening} / {t.n_employees}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="emp-top25-kpi-loss">
              <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500">€ marca perdida (Top 25)</div>
              <div className="font-mono-num font-bold text-lg mt-0.5 text-rose-700">{fmtEur(t.eur_marca_perdida)}</div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-100 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  <th className="p-2 text-left">Empleado</th>
                  <th className="p-2 text-right">Uds Top 25</th>
                  <th className="p-2 text-right">Marca</th>
                  <th className="p-2 text-right">Genérico</th>
                  <th className="p-2 text-right">% genérico</th>
                  <th className="p-2 text-right">vs equipo</th>
                  <th className="p-2 text-right">% anterior</th>
                  <th className="p-2 text-left">Mejora</th>
                  <EvoHeaders months={data.evolution_months || []} cls="p-2" light />
                  <th className="p-2 text-right">€ marca perdida</th>
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {data.employees.map((e) => (
                  <Fragment key={e.id}>
                    <tr className="border-b border-slate-100 hover:bg-slate-50" data-testid={`emp-top25-row-${e.id}`}>
                      <td className="p-2 font-semibold text-slate-800">{e.name}</td>
                      <td className="p-2 text-right font-mono-num">{e.units_total}</td>
                      <td className="p-2 text-right font-mono-num text-slate-500">{e.units_marca}</td>
                      <td className="p-2 text-right font-mono-num text-slate-500">{e.units_generico}</td>
                      <td className="p-2 text-right font-mono-num font-bold">{e.gen_pct}%</td>
                      <td className={`p-2 text-right font-mono-num ${e.delta_vs_team_pp > 0 ? "text-emerald-600" : e.delta_vs_team_pp < 0 ? "text-rose-600" : "text-slate-400"}`}>
                        {e.delta_vs_team_pp > 0 ? "+" : ""}{e.delta_vs_team_pp} pp
                      </td>
                      <td className="p-2 text-right font-mono-num text-slate-500">
                        {e.prev_gen_pct == null ? "—" : `${e.prev_gen_pct}%`}
                      </td>
                      <td className="p-2"><Trend d={e.delta_vs_prev_pp} /></td>
                      <EvoCells values={e.evolution} months={data.evolution_months || []} cls="p-2" testid={`emp-top25-evo-${e.id}`} />
                      <td className="p-2 text-right font-mono-num text-rose-700 font-semibold">{fmtEur(e.eur_marca_perdida)}</td>
                      <td className="p-2 text-right">
                        <button
                          onClick={() => setOpen(open === e.id ? null : e.id)}
                          className="inline-flex items-center gap-1 text-[11px] font-bold text-sky-700 hover:text-sky-900"
                          data-testid={`emp-top25-expand-${e.id}`}
                        >
                          {open === e.id ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                          {e.molecules_touched} molec.
                        </button>
                      </td>
                    </tr>
                    {open === e.id && (
                      <tr className="bg-slate-50" data-testid={`emp-top25-detail-${e.id}`}>
                        <td colSpan={10 + ((data.evolution_months || []).length >= 2 ? data.evolution_months.length + 1 : 0)} className="p-3">
                          <EmployeeEvolutionChart months={data.evolution_months || []} values={e.evolution} team={(data.evolution_months || []).map((m) => m.team_gen_pct)} name={e.name} testid={`emp-top25-chart-${e.id}`} />
                          <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-2">
                            Donde más marca pierde dentro del Top 25
                          </div>
                          <table className="w-full text-[11px]">
                            <thead className="text-slate-400 uppercase text-[9px]">
                              <tr>
                                <th className="p-1 text-left">#</th>
                                <th className="p-1 text-left">Molécula (G.H.)</th>
                                <th className="p-1 text-right">Uds marca</th>
                                <th className="p-1 text-right">Uds genérico</th>
                                <th className="p-1 text-right">% genérico</th>
                                <EvoHeaders months={data.evolution_months || []} cls="p-1" light />
                                <th className="p-1 text-right">€ marca</th>
                              </tr>
                            </thead>
                            <tbody>
                              {e.worst_molecules.map((m) => (
                                <tr key={m.gh} className="border-t border-slate-200">
                                  <td className="p-1 text-slate-400 font-mono-num">{m.rank}</td>
                                  <td className="p-1 font-semibold text-slate-700">{m.gh}</td>
                                  <td className="p-1 text-right font-mono-num">{m.marca_units}</td>
                                  <td className="p-1 text-right font-mono-num">{m.gen_units}</td>
                                  <td className="p-1 text-right font-mono-num">{m.gen_pct}%</td>
                                  <EvoCells values={m.evolution} months={data.evolution_months || []} cls="p-1" />
                                  <td className="p-1 text-right font-mono-num text-rose-700">{fmtEur(m.eur_marca)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
